// Permissions duck: actions, reducer, selectors, and a small evaluator.
//
// Shape: permissionsMap is an object keyed by resource name, each value is
// an object of actions -> boolean. Example:
// {
//   ravens: { 
//      read: true, 
//      write: true,
//      byId: { 
//         1: { trip: { share: true } },
//      }
//   },
//   settings: { 
//      read: true 
//    }
// }

// Initial state
const INITIAL_STATE = {
	permissionsMap: {}, // normalized capabilities map
	raw: [], // raw grantedPermissions array from server
    loaded: {}, // track what permissions have been loaded, e.g., { general: true, ravens: true }
};

// Action types
const ACTION_TYPES = {
	SET_GENERAL_PERMISSIONS: 'data/permissions/SET_GENERAL_PERMISSIONS',
    SET_RESOURCE_PERMISSIONS: 'data/permissions/SET_RESOURCE_PERMISSIONS',
	CLEAR_PERMISSIONS: 'data/permissions/CLEAR_PERMISSIONS',
	UPDATE_PERMISSION: 'data/permissions/UPDATE_PERMISSION',
    ADD_RESOURCE_PERMISSION: 'data/permissions/ADD_RESOURCE_PERMISSION',
    REMOVE_RESOURCE_PERMISSION: 'data/permissions/REMOVE_RESOURCE_PERMISSION',
};

// Action creators
export const setGeneralPermissions = (permissionsMap = {}, rawPermissions = []) => ({
	type: ACTION_TYPES.SET_GENERAL_PERMISSIONS,
	payload: { permissionsMap, rawPermissions },
});

export const setResourcePermissions = (resourceType, permissionsMap = {}, rawPermissions = []) => ({
	type: ACTION_TYPES.SET_RESOURCE_PERMISSIONS,
	payload: { resourceType, permissionsMap, rawPermissions },
});

export const clearPermissions = () => ({ type: ACTION_TYPES.CLEAR_PERMISSIONS });

export const updatePermission = (resource, actionName, value) => ({
	type: ACTION_TYPES.UPDATE_PERMISSION,
	payload: { resource, actionName, value },
});

export const addResourcePermission = (resourceType, resourceId, permissions) => ({
    type: ACTION_TYPES.ADD_RESOURCE_PERMISSION,
    payload: { resourceType, resourceId, permissions },
});

export const removeResourcePermission = (resourceType, resourceId) => ({
    type: ACTION_TYPES.REMOVE_RESOURCE_PERMISSION,
    payload: { resourceType, resourceId },
});

// Reducer
export default function reducer(state = INITIAL_STATE, action = {}) {
	switch (action.type) {
		case ACTION_TYPES.SET_GENERAL_PERMISSIONS: {
			const { permissionsMap = {}, rawPermissions = [] } = action.payload || {};
			return {
				...state,
				permissionsMap,
				raw: rawPermissions,
                loaded: {
                    ...state.loaded,
                    general: true,
                },
			};
		}

        case ACTION_TYPES.SET_RESOURCE_PERMISSIONS: {
			const { resourceType, permissionsMap = {}, rawPermissions = [] } = action.payload || {};
			
            // Keep resourceTypes general permissions like {ravens: {read: true}}
            const resourcePermissions = state.permissionsMap[resourceType] || {};
            // Add specific permissions like {ravens: {read: true, byId: {1: {read: true}}}}
            resourcePermissions.byId = {...permissionsMap}

            return {
				...state,
				permissionsMap: {
					...state.permissionsMap,
					[resourceType]: {
                        ...resourcePermissions,
					},
				},
				raw: [ ...state.raw, ...rawPermissions],
                loaded: {
                    ...state.loaded,
                    [resourceType]: true,
                },
			};
		}

		case ACTION_TYPES.CLEAR_PERMISSIONS:
			return INITIAL_STATE;

		case ACTION_TYPES.UPDATE_PERMISSION: {
			const { resource, actionName, value } = action.payload || {};
			if (!resource || !actionName) return state;
			const nextMap = { ...state.permissionsMap };
			const resourceMap = { ...(nextMap[resource] || {}) };
			if (value) {
				resourceMap[actionName] = true;
			} else {
				delete resourceMap[actionName];
			}
			nextMap[resource] = resourceMap;
			return {
				...state,
				permissionsMap: nextMap,
			};
		}

        case ACTION_TYPES.ADD_RESOURCE_PERMISSION: {
            const { resourceType, resourceId, permissions } = action.payload || {};
            if (!resourceType || !resourceId || !permissions) return state;

            const nextMap = { ...state.permissionsMap };
            const resourcePermissions = { ...(nextMap[resourceType] || {}) };
            const byIdPermissions = { ...(resourcePermissions.byId || {}) };

            byIdPermissions[resourceId] = permissions;
            resourcePermissions.byId = byIdPermissions;
            nextMap[resourceType] = resourcePermissions;

            return {
                ...state,
                permissionsMap: nextMap,
            };
        }

        case ACTION_TYPES.REMOVE_RESOURCE_PERMISSION: {
            const { resourceType, resourceId } = action.payload || {};
            if (!resourceType || !resourceId) return state;

            const nextMap = { ...state.permissionsMap };
            const resourcePermissions = { ...(nextMap[resourceType] || {}) };
            const byIdPermissions = { ...(resourcePermissions.byId || {}) };

            delete byIdPermissions[resourceId];
            resourcePermissions.byId = byIdPermissions;
            nextMap[resourceType] = resourcePermissions;

            return {
                ...state,
                permissionsMap: nextMap,
            };
        }

		default:
			return state;
	}
};

// Selectors
export const Selectors = {
	getMap: (state) => state?.data?.permissions?.permissionsMap ?? {},
    getRaw: (state) => state?.data?.permissions?.raw ?? [],
    isLoaded: (state, type = 'general') => state?.data?.permissions?.loaded?.[type] === true,
	can: (state, resource, actionName, context) => {
		const map = Selectors.getMap(state) || {};
        return checkPermission(map, { resource, action: actionName, context });
	},
};

// Permission evaluator helper
// Keep evaluation logic here so callers don't depend on raw permission syntax.
export const checkPermission = (permissionsMap = {}, { resource, action, context } = {}) => {
	if (!permissionsMap || !resource || !action) return false;

    const [mainResource, subResource] = resource.split('.');
    
	const resourceCaps = permissionsMap[mainResource];
	if (!resourceCaps) return false;
    
    const resourceId = context && context.resourceId;

	// Check for resource-specific permissions
	if (resourceId) {
        const resourceIdCaps = resourceCaps.byId?.[resourceId]; // raven with specific ID
		return resourceIdCaps?.[subResource]?.[action] === true; // raven.livestream.view
	}

	// Basic boolean capability check
	return resourceCaps[action] === true;

	// Future: resourceCaps[action] may be an object/array describing rules
	// and 'context' can be used to evaluate ABAC-style rules.
};

